import React, { useMemo } from 'react'
import { FaLongArrowAltRight } from "react-icons/fa";
import { FaLongArrowAltLeft } from "react-icons/fa";
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { calculateProductQuantity, getMurPlaquePlatreSurface } from '../../../Services/quoteCalculator';

const MurPlaquePlatreProduct = ({ quoteData }) => {
  // Récupérer les données depuis Redux
  const selectedProducts = useSelector(state => state.selectedProducts.selectedProducts);
  const selectedProject = useSelector(state => state.selectedProject.selectedProject);
  const sectionNames = useSelector(state => state.selectedProducts.sectionNames);
  const userInputs = useSelector(state => state.selectedProducts.userInputs);
  const selectedChoices = useSelector(state => state.selectedProducts.selectedChoices);
  const surfaceArea = useSelector(state => state.selectedProducts.surfaceArea);
  const projectType = useSelector(state => state.selectedProducts.projectType) || selectedProject?.project_type || 'MUR_PLAQUE_PLATRE';

  // Déterminer le type de projet (DOUBLAGE ou CLOISON) depuis les choix sélectionnés et les inputs
  const projectSubType = useMemo(() => {
    // D'abord chercher dans les choix
    const doublageChoice = selectedChoices.find(c => c.code === 'TYPE_DOUBLAGE' || c.code?.includes('DOUBLAGE'));
    const cloisonChoice = selectedChoices.find(c => c.code === 'TYPE_CLOISON' || c.code?.includes('CLOISON'));
    
    if (doublageChoice) return 'DOUBLAGE';
    if (cloisonChoice) return 'CLOISON';
    
    // Fallback : chercher dans les labels des choix
    const doublageLabel = selectedChoices.find(c => c.label?.toLowerCase().includes('doublage'));
    const cloisonLabel = selectedChoices.find(c => c.label?.toLowerCase().includes('cloison'));
    
    if (doublageLabel) return 'DOUBLAGE';
    if (cloisonLabel) return 'CLOISON';
    
    // Fallback : chercher dans les inputs
    if (userInputs) {
      for (const [sectionId, sectionInputs] of Object.entries(userInputs)) {
        for (const [key, inputData] of Object.entries(sectionInputs)) {
          const label = typeof inputData === 'object' && inputData?.label 
            ? inputData.label.toLowerCase() 
            : key.toLowerCase();
          if (label.includes('doublage')) return 'DOUBLAGE';
          if (label.includes('cloison')) return 'CLOISON';
        }
      }
    }
    
    return null;
  }, [selectedChoices, userInputs]);

  // Liste de tous les produits avec leurs codes et formules selon le type de projet
  const allProducts = useMemo(() => {
    if (!projectSubType) return [];

    const products = [];

    if (projectSubType === 'DOUBLAGE') {
      products.push(
        { code: '334170', name: 'Plaque de plâtre', formula: 'ARRONDI.SUP (Surface x 1,05 / 3)', unit: 'UN' },
        { code: '240503', name: 'Plaque de plâtre', formula: 'ARRONDI.SUP (Surface x 2,40 / 5,30)', unit: 'UN' },
        { code: '322179', name: 'Plaque de plâtre', formula: 'ARRONDI.SUP (Surface x 1,10 / 3)', unit: 'UN' },
        { code: '247942', name: 'Montants', formula: 'ARRONDI.SUP (Surface x 11 / 1000)', unit: 'ML' },
        { code: '247854', name: 'Rail', formula: 'ARRONDI.SUP (Surface x 1,40 / 150)', unit: 'ML' },
        { code: '335302', name: 'Vis', formula: 'ARRONDI.SUP (Surface x 0,47 / 22)', unit: 'BT' },
        { code: '335138', name: 'Isolant (100mm)', formula: 'Surface / 50 (si épaisseur 100mm)', unit: 'UN', condition: 'isolationEpaisseur === 100' },
        { code: '335141', name: 'Isolant (120-180mm)', formula: 'Surface / 50 (si épaisseur 120-180mm)', unit: 'UN', condition: 'isolationEpaisseur >= 120 && <= 180' },
        { code: '339736', name: 'Paille de riz', formula: 'Surface / 0,696 (si 100mm ou 145mm)', unit: 'UN', condition: 'paille && (100mm || 145mm)' },
        { code: '246493', name: 'Laine de roche (120mm)', formula: 'Surface / 3,810', unit: 'UN', condition: 'roche && 120mm' },
        { code: '336986', name: 'Laine de roche (145mm)', formula: 'Surface / 3,051', unit: 'UN', condition: 'roche && 145mm' },
        { code: '326190', name: 'Fibre de bois (100mm)', formula: 'Surface / 4,246', unit: 'UN', condition: 'fibre && 100mm' },
        { code: '326191', name: 'Fibre de bois (120mm)', formula: 'Surface / 3,538', unit: 'UN', condition: 'fibre && 120mm' },
        { code: '326192', name: 'Fibre de bois (145mm)', formula: 'Surface / 2,830', unit: 'UN', condition: 'fibre && 145mm' },
        { code: '326193', name: 'Fibre de bois (160mm)', formula: 'Surface / 2,830', unit: 'UN', condition: 'fibre && 160mm' },
        { code: '329985', name: 'Laine de verre (100mm)', formula: 'Surface / 4,050', unit: 'UN', condition: 'verre && 100mm' },
        { code: '329984', name: 'Laine de verre (120mm)', formula: 'Surface / 3,240', unit: 'UN', condition: 'verre && 120mm' },
        { code: '329983', name: 'Laine de verre (140mm)', formula: 'Surface / 3,240', unit: 'UN', condition: 'verre && 140mm' },
        { code: '329976', name: 'Laine de verre (160mm)', formula: 'Surface / 3,240', unit: 'UN', condition: 'verre && 160mm' }
      );
    } else if (projectSubType === 'CLOISON') {
      products.push(
        { code: '334170', name: 'Plaque de plâtre', formula: 'ARRONDI.SUP (Surface x 2,10 / 3)', unit: 'UN' },
        { code: '247942', name: 'Montants', formula: 'ARRONDI.SUP (Surface x 22 / 1000)', unit: 'ML' },
        { code: '247854', name: 'Rail', formula: 'ARRONDI.SUP (Surface x 2,80 / 150)', unit: 'ML' },
        { code: '335302', name: 'Vis', formula: 'ARRONDI.SUP (Surface x 0,94 / 22)', unit: 'BT' },
        { code: '240474', name: 'Isolant (45mm ou sans)', formula: 'ARRONDI.SUP (Surface x 2,00 / 2,80)', unit: 'UN', condition: 'pas isolant || 45mm' },
        { code: '240483', name: 'Isolant (70mm)', formula: 'ARRONDI.SUP (Surface x 2,00 / 2,80)', unit: 'UN', condition: '70mm' },
        { code: '240500', name: 'Isolant (100mm)', formula: 'ARRONDI.SUP (Surface x 2,00 / 3,00)', unit: 'UN', condition: '100mm' },
        { code: '240468', name: 'Isolant (45mm ou sans)', formula: 'ARRONDI.SUP (Surface x 0,90 / 3,00)', unit: 'UN', condition: 'pas isolant || 45mm' },
        { code: '240469', name: 'Isolant (70mm)', formula: 'ARRONDI.SUP (Surface x 0,90 / 3,00)', unit: 'UN', condition: '70mm' },
        { code: '240498', name: 'Isolant (100mm)', formula: 'ARRONDI.SUP (Surface x 0,90 / 3,00)', unit: 'UN', condition: '100mm' },
        { code: '339736', name: 'Paille de riz (100mm)', formula: 'Surface / 0,696', unit: 'UN', condition: 'paille && 100mm' },
        { code: '236198', name: 'Laine de roche (40mm)', formula: 'Surface / 6,000', unit: 'UN', condition: 'roche && 40mm' },
        { code: '326187', name: 'Fibre de bois (40mm)', formula: 'Surface / 10,614', unit: 'UN', condition: 'fibre && 40mm' },
        { code: '326188', name: 'Fibre de bois (60mm)', formula: 'Surface / 7,076', unit: 'UN', condition: 'fibre && 60mm' },
        { code: '326190', name: 'Fibre de bois (100mm)', formula: 'Surface / 4,260', unit: 'UN', condition: 'fibre && 100mm' },
        { code: '329975', name: 'Laine de verre (45mm)', formula: 'Surface / 19,200', unit: 'UN', condition: 'verre && 45mm' },
        { code: '329974', name: 'Laine de verre (70mm)', formula: 'Surface / 12,600', unit: 'UN', condition: 'verre && 70mm' },
        { code: '329985', name: 'Laine de verre (100mm)', formula: 'Surface / 4,050', unit: 'UN', condition: 'verre && 100mm' }
      );
    }

    return products;
  }, [projectSubType]);

  // Calculer la surface selon le type de projet
  const calculatedSurface = useMemo(() => {
    if (!projectSubType) return 0;
    const surface = getMurPlaquePlatreSurface(userInputs || {}, selectedChoices || []);
    console.log('🔍 Surface calculée MUR_PLAQUE_PLATRE:', surface, 'm²');
    return surface;
  }, [projectSubType, userInputs, selectedChoices]);
  
  // Récupérer les paramètres pour affichage détaillé
  const projectParams = useMemo(() => {
    if (!projectSubType) return null;
    // Importer la fonction getMurPlaquePlatreParams (elle n'est pas exportée, on va la recréer ici)
    // Pour l'instant, on va juste extraire les infos depuis userInputs et selectedChoices
    const params = {
      hauteurSousPlafond: null,
      hauteurBassePlafond: null,
      hauteurHautePlafond: null,
      metreLineaire: null,
      longueurTotale: null,
      plafondType: null,
      surfaceMenuiseries: null
    };
    
    // Chercher dans les choix pour le type de plafond
    const plafondPlat = selectedChoices.find(c => c.code === 'PLAFOND_PLAT' || c.code?.includes('PLAT'));
    const plafondRampant = selectedChoices.find(c => c.code === 'PLAFOND_RAMPANT' || c.code?.includes('RAMPANT'));
    params.plafondType = plafondPlat ? 'PLAT' : (plafondRampant ? 'RAMPANT' : null);
    
    // Chercher dans les inputs
    if (userInputs) {
      for (const [sectionId, sectionInputs] of Object.entries(userInputs)) {
        for (const [key, inputData] of Object.entries(sectionInputs)) {
          const value = typeof inputData === 'object' && inputData?.value !== undefined 
            ? inputData.value 
            : inputData;
          const label = typeof inputData === 'object' && inputData?.label 
            ? inputData.label.toLowerCase() 
            : key.toLowerCase();
          
          // Hauteurs - Plus flexible
          if ((label.includes('hauteur') && label.includes('sous') && label.includes('plafond')) ||
              label.includes('hauteur sous plafond') || 
              label.includes('hauteur sous le plafond')) {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 0) {
              params.hauteurSousPlafond = numValue;
            }
          }
          if ((label.includes('hauteur') && label.includes('basse')) || 
              label.includes('hauteur_basse')) {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 0) {
              params.hauteurBassePlafond = numValue;
            }
          }
          if ((label.includes('hauteur') && label.includes('haute')) || 
              label.includes('hauteur_haute')) {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 0) {
              params.hauteurHautePlafond = numValue;
            }
          }
          // Longueurs - Plus flexible
          if ((label.includes('mètre') || label.includes('metre') || label.includes('ml')) && 
              (label.includes('linéaire') || label.includes('lineaire') || label.includes('total'))) {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 0) {
              params.metreLineaire = numValue;
            }
          }
          if ((label.includes('longueur') && label.includes('totale')) || 
              label.includes('longueur_totale')) {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 0) {
              params.longueurTotale = numValue;
            }
          }
          // Surface menuiseries - Plus flexible
          if ((label.includes('surface') && label.includes('totale') && label.includes('menuiseries')) ||
              (label.includes('surface') && label.includes('menuiseries')) ||
              (label.includes('menuiseries') && (label.includes('m²') || label.includes('m2') || label.includes('surface')))) {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 0) {
              params.surfaceMenuiseries = numValue;
            }
          }
        }
      }
    }
    
    return params;
  }, [projectSubType, userInputs, selectedChoices]);

  // Calculer les quantités pour tous les produits
  const calculatedProducts = useMemo(() => {
    if (!projectSubType || calculatedSurface <= 0) return [];

    return allProducts.map(product => {
      const quantity = calculateProductQuantity(
        product.code,
        projectType,
        calculatedSurface,
        userInputs || {},
        selectedChoices || []
      );
      
      return {
        ...product,
        quantity: quantity > 0 ? quantity : 0
      };
    }).filter(p => p.quantity > 0); // Filtrer les produits avec quantité 0
  }, [allProducts, projectSubType, calculatedSurface, projectType, userInputs, selectedChoices]);

  // Fonction pour récupérer le nom d'une section
  const getSectionName = (sectionId) => {
    if (sectionNames && sectionNames[sectionId]) {
      return sectionNames[sectionId];
    }
    
    if (selectedProject && selectedProject.steps) {
      for (const step of selectedProject.steps) {
        if (step.sections) {
          for (const section of step.sections) {
            if (section.id === sectionId) {
              return section.name || `Section ${sectionId}`;
            }
          }
        }
      }
    }
    
    return `Section ${sectionId}`;
  };

  // Fonction pour récupérer les détails du produit
  const getProductDetails = (item) => {
    if (item.name && item.price !== undefined) {
      return {
        name: item.name,
        image: item.image || "/assets/images/lalliard-lame-terrasse-focus_pin_du_nord_marron.png",
        price: item.price,
        quantity: item.quantity || 0,
        sku: item.sku || item.product_sku || ''
      };
    }
    
    // Si c'est un item de quoteData, extraire les informations
    if (item.product_name) {
      return {
        name: item.product_name,
        image: item.product_image || "/assets/images/lalliard-lame-terrasse-focus_pin_du_nord_marron.png",
        price: parseFloat(item.product_price) || 0,
        quantity: parseInt(item.quantity) || 0,
        sku: item.product_sku || item.sku || ''
      };
    }
    
    return {
      name: 'Produit inconnu',
      image: "/assets/images/lalliard-lame-terrasse-focus_pin_du_nord_marron.png",
      price: 0,
      quantity: 0,
      sku: ''
    };
  };

  // Fonction pour calculer la quantité selon les formules MUR_PLAQUE_PLATRE
  const getCalculatedQuantity = (item) => {
    const pd = getProductDetails(item);
    const sku = pd.sku || item.sku || item.product_sku || '';
    
    if (!sku) {
      console.warn('⚠️ Pas de SKU pour le produit:', pd.name, '→ utilisation quantité originale:', pd.quantity);
      return pd.quantity || 1;
    }
    
    // Utiliser calculateProductQuantity avec le SKU comme productType
    const calculatedQty = calculateProductQuantity(
      sku,
      projectType,
      calculatedSurface,
      userInputs || {},
      selectedChoices || []
    );
    
    console.log(`✅ Calcul quantité MUR_PLAQUE_PLATRE: ${pd.name} (SKU: ${sku}) → ${calculatedQty} (surface: ${calculatedSurface} m²)`);
    
    return calculatedQty;
  };

  // Préparer les items à afficher
  const itemsToDisplay = quoteData?.items || selectedProducts || [];

  // Grouper les items par section
  const groupedItems = itemsToDisplay.reduce((acc, item) => {
    const sectionId = item.section || item.section_id;
    const sectionName = item.section_name || getSectionName(sectionId);
    
    if (!acc[sectionName]) {
      acc[sectionName] = [];
    }
    acc[sectionName].push(item);
    return acc;
  }, {});

  return (
    <div>
      <div className="page">
        <div className="container">
          <div className="row px-5">
            <div className="col-md-5 col-sm-5 col-12 px-0">
              <div className="image-terrasse" style={{ height: 380 }}>
                <img
                  src="/assets/images/Capture_terrace.jpg.png"
                  alt=""
                  className="w-100 h-100"
                />
              </div>
            </div>
            <div className="col-md-7 col-sm-7 col-12 px-0">
              {/* Récapitulatif des informations renseignées */}
              <div className="mt-4 mb-4">
                <h4 className="mb-3">Récapitulatif du projet</h4>
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title">Informations sélectionnées</h5>
                    <ul className="list-unstyled">
                      <li><strong>Type de projet :</strong> {projectSubType || 'Non sélectionné'}</li>
                      <li><strong>Surface calculée :</strong> {calculatedSurface > 0 ? `${calculatedSurface.toFixed(2)} m²` : 'Non calculable (dimensions manquantes)'}</li>
                      <li><strong>Nombre de choix sélectionnés :</strong> {selectedChoices?.length || 0}</li>
                      <li><strong>Nombre d'inputs renseignés :</strong> {Object.keys(userInputs || {}).length}</li>
                    </ul>
                    
                    {selectedChoices && selectedChoices.length > 0 && (
                      <div className="mt-3">
                        <h6>Choix sélectionnés :</h6>
                        <ul className="list-unstyled">
                          {selectedChoices.map((choice, idx) => (
                            <li key={idx}>• {choice.label || choice.code || 'Choix sans nom'}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {userInputs && Object.keys(userInputs).length > 0 && (
                      <div className="mt-3">
                        <h6>Dimensions renseignées :</h6>
                        <ul className="list-unstyled">
                          {Object.entries(userInputs).map(([sectionId, sectionInputs]) => {
                            return Object.entries(sectionInputs).map(([key, inputData]) => {
                              const value = typeof inputData === 'object' && inputData?.value !== undefined 
                                ? inputData.value 
                                : inputData;
                              const label = typeof inputData === 'object' && inputData?.label 
                                ? inputData.label 
                                : key;
                              return (
                                <li key={`${sectionId}-${key}`}>
                                  <strong>{label} :</strong> {value}
                                </li>
                              );
                            });
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Affichage des calculs selon les formules du PDF */}
              {projectSubType ? (
                <div>
                  {/* Tableau des calculs détaillés */}
                  {calculatedSurface > 0 && calculatedProducts.length > 0 ? (
                    <div className="mt-4 mb-4">
                      <h4 className="mb-3">Calculs détaillés (formule {projectSubType})</h4>
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Produit</th>
                              <th>Calcul</th>
                              <th>Quantité</th>
                            </tr>
                          </thead>
                          <tbody>
                            {calculatedProducts.map((product, idx) => (
                              <tr key={`calc-${idx}`}>
                                <td>{product.name}</td>
                                <td>{product.formula}</td>
                                <td>{product.quantity} {product.unit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 mb-4">
                      <h4 className="mb-3">Calculs détaillés (formule {projectSubType})</h4>
                      <div className="alert alert-info">
                        <p>Les calculs nécessitent que les dimensions du projet soient renseignées.</p>
                        <p>Surface calculée : {calculatedSurface > 0 ? `${calculatedSurface.toFixed(2)} m²` : '0 m² (dimensions manquantes)'}</p>
                        {calculatedSurface <= 0 && projectParams && (
                          <div className="mt-2">
                            <p><strong>Paramètres détectés :</strong></p>
                            <ul className="mb-3">
                              <li>Type de plafond : {projectParams.plafondType || '❌ Non détecté'}</li>
                              {projectSubType === 'DOUBLAGE' && (
                                <>
                                  <li>Hauteur sous plafond : {projectParams.hauteurSousPlafond || '❌ Non renseigné'}</li>
                                  <li>Hauteur basse plafond : {projectParams.hauteurBassePlafond || '❌ Non renseigné'}</li>
                                  <li>Hauteur haute plafond : {projectParams.hauteurHautePlafond || '❌ Non renseigné'}</li>
                                  <li>Mètre linéaire : {projectParams.metreLineaire || '❌ Non renseigné'}</li>
                                </>
                              )}
                              {projectSubType === 'CLOISON' && (
                                <>
                                  <li>Hauteur sous plafond : {projectParams.hauteurSousPlafond || '❌ Non renseigné'}</li>
                                  <li>Hauteur basse plafond : {projectParams.hauteurBassePlafond || '❌ Non renseigné'}</li>
                                  <li>Hauteur haute plafond : {projectParams.hauteurHautePlafond || '❌ Non renseigné'}</li>
                                  <li>Longueur totale : {projectParams.longueurTotale || '❌ Non renseigné'}</li>
                                </>
                              )}
                              <li>Surface menuiseries : {projectParams.surfaceMenuiseries ? `${projectParams.surfaceMenuiseries} m² ✅` : '❌ Non renseigné'}</li>
                            </ul>
                            <p className="mt-2"><strong>Veuillez renseigner les éléments manquants :</strong></p>
                            {projectSubType === 'DOUBLAGE' && (
                              <ul>
                                {!projectParams.plafondType && <li>Type de plafond (PLAT ou RAMPANT)</li>}
                                {projectParams.plafondType === 'PLAT' && !projectParams.hauteurSousPlafond && (
                                  <li>Hauteur sous plafond (pour plafond plat)</li>
                                )}
                                {projectParams.plafondType === 'RAMPANT' && (!projectParams.hauteurBassePlafond || !projectParams.hauteurHautePlafond) && (
                                  <li>Hauteur basse et haute plafond (pour plafond rampant)</li>
                                )}
                                {!projectParams.metreLineaire && <li>Mètre linéaire de projet</li>}
                              </ul>
                            )}
                            {projectSubType === 'CLOISON' && (
                              <ul>
                                {!projectParams.plafondType && <li>Type de plafond (PLAT ou RAMPANT)</li>}
                                {projectParams.plafondType === 'PLAT' && !projectParams.hauteurSousPlafond && (
                                  <li>Hauteur sous plafond (pour plafond plat)</li>
                                )}
                                {projectParams.plafondType === 'RAMPANT' && (!projectParams.hauteurBassePlafond || !projectParams.hauteurHautePlafond) && (
                                  <li>Hauteur basse et haute plafond (pour plafond rampant)</li>
                                )}
                                {!projectParams.longueurTotale && <li>Longueur totale de cloison</li>}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Affichage des produits sélectionnés si disponibles */}
                  {itemsToDisplay.length > 0 && (
                    <div className="mt-5">
                      <h4 className="mb-3">Produits sélectionnés et soumis</h4>
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Produit</th>
                              <th>Prix unitaire (€)</th>
                              <th>Quantité</th>
                              <th>Total (€)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itemsToDisplay.map((item, idx) => {
                              const pd = getProductDetails(item);
                              const computedQty = calculatedSurface > 0 ? getCalculatedQuantity(item) : (pd.quantity || 0);
                              const lineTotal = (pd.price || 0) * (computedQty || 0);
                              
                              return (
                                <tr key={`summary-${idx}`}>
                                  <td>{pd.name}</td>
                                  <td>{pd.price ? pd.price.toFixed(2) : 'N/A'}</td>
                                  <td>{computedQty}</td>
                                  <td>{lineTotal.toFixed(2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr>
                              <th colSpan={3} className="text-end">Total</th>
                              <th>
                                {(() => {
                                  const total = itemsToDisplay.reduce((sum, item) => {
                                    const pd = getProductDetails(item);
                                    const computedQty = calculatedSurface > 0 ? getCalculatedQuantity(item) : (pd.quantity || 0);
                                    return sum + ((pd.price || 0) * (computedQty || 0));
                                  }, 0);
                                  return total.toFixed(2);
                                })()} €
                              </th>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-4 mb-4">
                  <div className="alert alert-warning">
                    <h5>Type de projet non détecté</h5>
                    <p>Veuillez sélectionner un type de projet (DOUBLAGE ou CLOISON) dans les étapes précédentes pour voir les calculs.</p>
                    <p className="mb-0"><strong>Choix disponibles :</strong></p>
                    <ul className="mb-0">
                      {selectedChoices && selectedChoices.length > 0 ? (
                        selectedChoices.map((choice, idx) => (
                          <li key={idx}>{choice.label || choice.code || 'Choix sans nom'}</li>
                        ))
                      ) : (
                        <li>Aucun choix sélectionné</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Bouton retour à l'accueil */}
              <div className="mt-4 mb-4 px-4">
                <Link to="/" className="btn btn-outline-secondary rounded-0">
                  <span className="text-2">Retour à l'accueil</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MurPlaquePlatreProduct

